import {inject, Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {FulfillmentType} from '@frontend/directus';
import {ProviderColumn} from '../table/provider-results-table/provider-results-table.types';
import {SubcriteriaAttributeScoreData} from './subcriteria-attribute-scores.types';
import {getIconForImportanceScore} from './subcriteria-attribute-scores-importance-icon.util';
import {getMaxScore} from '../../utils/rfd-evaluation-score.utils';

export interface HeatmapCellData {
  value: [number, number, number]; // [providerIndex, rowIndex, scoreValue]
  providerLabel: string;
  attributeLabel: string;
  importanceLabel: string;
  importanceIcon: string;
  providerValue: number;
  scoreValue: number;
  /** Gap before 1-x transformation: (customer * (1-provider)) / scaleFactor. Used for badge/tooltip display. */
  gapValue?: number;
  /** Table-compatible weighted score - matches provider-results-table (normalizedScore × attributeWeight). */
  tableScoreValue?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SubcriteriaAttributeScoresHeatmapCalculationService {
  private readonly transloco = inject(TranslocoService);

  /**
   * Refactored to be a pure transformation function.
   * Perfectly suited for use inside an Angular computed() signal.
   */
  calculateHeatmapData(
    rows: SubcriteriaAttributeScoreData[],
    selectedProviders: ProviderColumn[],
    allProviders: ProviderColumn[],
    isExponentialScale: boolean,
    rfdMode: boolean,
  ): HeatmapCellData[] {
    if (rfdMode) {
      return this.calculateRfdHeatmapData(rows, selectedProviders, isExponentialScale);
    }

    const maxValuesByRow = this.computeMaxValuesMap(rows, allProviders);
    const minMaxByRow = this.computeMinMaxValuesMap(rows, allProviders);

    return rows.flatMap((row, rowIndex) =>
      selectedProviders
        .map((provider, providerIndex) => {
          const providerValue = row.providerValuesByHrToolId.get(provider.hrToolDirectusId);
          const customerRating = row.customerAverageRating;

          if (providerValue == null) return null;

          const attributeWeight = customerRating ?? 1.0;
          const maxVal = maxValuesByRow.get(rowIndex);
          const normalizedProvider = this.normalizeScore(
            providerValue,
            row.fulfillmentType,
            maxVal,
          );

          if (normalizedProvider === null) return null;

          const scaleFactor = isExponentialScale ? 16 : 4;
          const gap =
            customerRating != null ? (customerRating * (1 - normalizedProvider)) / scaleFactor : 0;
          const scoreValue = 1 - Math.min(1, Math.max(0, gap));

          const minMax = minMaxByRow.get(rowIndex);
          const normalizedScore = this.computeTableNormalizedScore(
            providerValue,
            row.fulfillmentType,
            minMax,
          );
          const tableScoreValue =
            normalizedScore !== null ? normalizedScore * attributeWeight : undefined;

          const cell: HeatmapCellData = {
            value: [providerIndex, rowIndex, scoreValue],
            providerLabel: provider.translatedName,
            attributeLabel: row.label,
            importanceLabel: this.getImportanceLabel(row.importanceLabelTranslationKey),
            importanceIcon: getIconForImportanceScore(row.importanceScore),
            providerValue,
            scoreValue,
            ...(customerRating != null ? {gapValue: gap} : {}),
            ...(tableScoreValue !== undefined ? {tableScoreValue} : {}),
          };
          return cell;
        })
        .filter((cell): cell is HeatmapCellData => cell !== null),
    );
  }

  private computeMaxValuesMap(
    rows: SubcriteriaAttributeScoreData[],
    providers: ProviderColumn[],
  ): Map<number, number> {
    const maxMap = new Map<number, number>();

    rows.forEach((row, idx) => {
      if (this.isQuantitativeType(row.fulfillmentType)) {
        const values = this.getProviderValuesForRow(row, providers);
        maxMap.set(idx, values.length ? Math.max(...values) : 0);
      }
    });

    return maxMap;
  }

  private computeMinMaxValuesMap(
    rows: SubcriteriaAttributeScoreData[],
    providers: ProviderColumn[],
  ): Map<number, {min: number; max: number}> {
    const map = new Map<number, {min: number; max: number}>();

    rows.forEach((row, idx) => {
      if (this.isQuantitativeType(row.fulfillmentType)) {
        const values = this.getProviderValuesForRow(row, providers);
        if (values.length > 0) {
          map.set(idx, {min: Math.min(...values), max: Math.max(...values)});
        }
      }
    });

    return map;
  }

  private getProviderValuesForRow(
    row: SubcriteriaAttributeScoreData,
    providers: ProviderColumn[],
  ): number[] {
    return providers
      .map((p) => row.providerValuesByHrToolId.get(p.hrToolDirectusId))
      .filter((v): v is number => v != null);
  }

  private isQuantitativeType(type: FulfillmentType | null | undefined): boolean {
    return type === FulfillmentType.NUMERIC || type === FulfillmentType.PERCENTAGE;
  }

  private isUnitInterval(value: number): boolean {
    return value >= 0 && value <= 1;
  }

  private normalizeScore(
    value: number,
    type: FulfillmentType | null | undefined,
    max?: number,
  ): number | null {
    switch (type) {
      case FulfillmentType.TERNARY:
        return this.isUnitInterval(value) ? value : null;
      case FulfillmentType.BOOLEAN:
        return [0, 1].includes(value) ? value : null;
      case FulfillmentType.NUMERIC:
      case FulfillmentType.PERCENTAGE: {
        const safeMax = max ?? 0;
        return safeMax > 0 ? Math.min(1, Math.max(0, value / safeMax)) : 0;
      }
      default:
        return null;
    }
  }

  /**
   * Computes table-compatible normalized score (0-1) using same logic as provider-results-table:
   * min-max normalization for numeric/percentage, raw value for ternary/boolean.
   */
  private computeTableNormalizedScore(
    value: number,
    type: FulfillmentType | null | undefined,
    minMax?: {min: number; max: number},
  ): number | null {
    switch (type) {
      case FulfillmentType.TERNARY:
        return this.isUnitInterval(value) ? value : null;
      case FulfillmentType.BOOLEAN:
        return [0, 1].includes(value) ? value : null;
      case FulfillmentType.NUMERIC:
      case FulfillmentType.PERCENTAGE: {
        if (!minMax) return null;
        const {min, max} = minMax;
        const range = max - min;
        if (range === 0) return value === min ? 0 : 1;
        return Math.min(1, Math.max(0, (value - min) / range));
      }
      default:
        return null;
    }
  }

  // Parameter updated to allow null to fix TS2345
  private getImportanceLabel(key: string | null | undefined): string {
    return key ? this.transloco.translate(key) : '-';
  }

  private calculateRfdHeatmapData(
    rows: SubcriteriaAttributeScoreData[],
    selectedProviders: ProviderColumn[],
    isExponentialScale: boolean,
  ): HeatmapCellData[] {
    const maxScore = getMaxScore(isExponentialScale);

    return rows.flatMap((row, rowIndex) =>
      selectedProviders
        .map((provider, providerIndex) => {
          const providerValue = row.providerValuesByHrToolId.get(provider.hrToolDirectusId);
          if (providerValue == null) return null;

          const gradientValue =
            maxScore > 0 ? Math.min(1, Math.max(0, providerValue / maxScore)) : 0;

          const cell: HeatmapCellData = {
            value: [providerIndex, rowIndex, gradientValue],
            providerLabel: provider.translatedName,
            attributeLabel: row.label,
            importanceLabel: '-',
            importanceIcon: '',
            providerValue,
            scoreValue: providerValue,
            tableScoreValue: providerValue,
          };
          return cell;
        })
        .filter((cell): cell is HeatmapCellData => cell !== null),
    );
  }
}
